/**
 * useOpportunityRadar — encapsulates all radar UI state:
 *   view (matrix · table · card)
 *   filters (text, dept, BU, status, risk, effort, complianceImpact)
 *   sort     (field + direction)
 *   savedViews (preset filter+sort combos)
 *
 * Pure logic; no UI. The page mounts this and renders.
 */
import { useCallback, useMemo, useState } from "react";

const DEFAULT_FILTERS = {
  q: "",
  department: "all",
  businessUnit: "all",
  status: "all",
  risk: "all",
  effort: "all",
  complianceImpact: "all",
};

const SAVED_VIEW_PRESETS = [
  {
    id: "high-roi-low-risk",
    label: "High ROI · Low Risk",
    filters: { ...DEFAULT_FILTERS, risk: "low" },
    sort: { field: "estimatedValueAud", dir: "desc" },
  },
  {
    id: "quick-wins",
    label: "Quick Wins (S effort)",
    filters: { ...DEFAULT_FILTERS, effort: "S" },
    sort: { field: "priorityScore", dir: "desc" },
  },
  {
    id: "regulated-decisioning",
    label: "Regulated Decisioning",
    filters: { ...DEFAULT_FILTERS, complianceImpact: "high" },
    sort: { field: "riskScore", dir: "desc" },
  },
  {
    id: "ready-to-deploy",
    label: "Ready for Approval",
    filters: { ...DEFAULT_FILTERS, status: "endorsed" },
    sort: { field: "priorityScore", dir: "desc" },
  },
];

const SORT_OPTIONS = [
  { field: "priorityScore", label: "Priority (highest)", dir: "desc" },
  { field: "estimatedValueAud", label: "Est. value (highest)", dir: "desc" },
  { field: "roiScore", label: "ROI score (highest)", dir: "desc" },
  { field: "riskScore", label: "Risk (lowest first)", dir: "asc" },
  { field: "effortScore", label: "Effort (lowest first)", dir: "asc" },
  { field: "paybackMonths", label: "Payback (fastest)", dir: "asc" },
];

export function useOpportunityRadar(opportunities) {
  const [view, setView] = useState("matrix"); // matrix | table | card
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState(SORT_OPTIONS[0]);
  const [activeSavedView, setActiveSavedView] = useState(null);

  const updateFilter = useCallback((key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setActiveSavedView(null);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setActiveSavedView(null);
  }, []);

  const applySavedView = useCallback((id) => {
    const v = SAVED_VIEW_PRESETS.find((sv) => sv.id === id);
    if (!v) return;
    setFilters(v.filters);
    setSort(SORT_OPTIONS.find((s) => s.field === v.sort.field) || SORT_OPTIONS[0]);
    setActiveSavedView(id);
  }, []);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return opportunities.filter((o) => {
      if (q) {
        const hay =
          `${o.title} ${o.department} ${o.businessUnit} ${o.owner} ${o.recommendedApproach}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.department !== "all" && o.department !== filters.department) return false;
      if (filters.businessUnit !== "all" && o.businessUnit !== filters.businessUnit) return false;
      if (filters.status !== "all" && o.status !== filters.status) return false;
      if (filters.risk !== "all" && o.risk !== filters.risk) return false;
      if (filters.effort !== "all" && o.effort !== filters.effort) return false;
      if (filters.complianceImpact !== "all" && o.complianceImpact !== filters.complianceImpact)
        return false;
      return true;
    });
  }, [opportunities, filters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sort.field] ?? 0;
      const bv = b[sort.field] ?? 0;
      return sort.dir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [filtered, sort]);

  // Tenant-aware filter option lists — derived from the actual
  // opportunities so the BU/Department dropdowns reflect the active
  // tenant (banking BUs for Acme, insurance BUs for Northwind) instead
  // of a hard-coded global list.
  const deptOptions = useMemo(() => {
    const set = new Set();
    opportunities.forEach((o) => o.department && set.add(o.department));
    return [
      { value: "all", label: "All departments" },
      ...Array.from(set).sort().map((d) => ({ value: d, label: d })),
    ];
  }, [opportunities]);

  const buOptions = useMemo(() => {
    const set = new Set();
    opportunities.forEach((o) => o.businessUnit && set.add(o.businessUnit));
    return [
      { value: "all", label: "All units" },
      ...Array.from(set).sort().map((b) => ({ value: b, label: b })),
    ];
  }, [opportunities]);

  return {
    view,
    setView,
    filters,
    updateFilter,
    resetFilters,
    sort,
    setSort,
    sortOptions: SORT_OPTIONS,
    savedViews: SAVED_VIEW_PRESETS,
    activeSavedView,
    applySavedView,
    items: sorted,
    totalCount: opportunities.length,
    matchedCount: sorted.length,
    deptOptions,
    buOptions,
  };
}
